import React, { useState, useEffect } from 'react';
import api from '../../../components/api';
import './ContextPanel.css';

const ContextPanel = ({ projectId, guidelineContexts, referenceContexts, onContextsUpdate, onClose }) => {
    const [contexts, setContexts] = useState([]);
    const [selectedTab, setSelectedTab] = useState('guideline'); // 'guideline' ou 'reference'
    const [isEditing, setIsEditing] = useState(false);
    const [editingContext, setEditingContext] = useState(null);
    const [newContextTitle, setNewContextTitle] = useState('');
    const [newContextContent, setNewContextContent] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Limites de caractères pour les guidelines
    const GUIDELINE_MAX_LENGTH = 1000;
    
    // Initialiser les contextes à partir des props
    useEffect(() => {
        if (selectedTab === 'guideline' && guidelineContexts) {
            setContexts(guidelineContexts);
        } else if (selectedTab === 'reference' && referenceContexts) {
            setContexts(referenceContexts);
        }
    }, [selectedTab, guidelineContexts, referenceContexts]);

    // Gérer l'ajout d'un nouveau contexte
    const handleAddContext = () => {
        setIsEditing(true);
        setEditingContext(null);
        setNewContextTitle('');
        setNewContextContent('');
    };

    // Gérer l'édition d'un contexte existant
    const handleEditContext = (context) => {
        setIsEditing(true);
        setEditingContext(context);
        setNewContextTitle(context.title);
        setNewContextContent(context.content);
    };

    // Sauvegarder un contexte (nouveau ou existant)
    const handleSaveContext = async () => {
        try {
            setIsLoading(true);
            setError('');

            // Vérifier la longueur pour les guidelines
            if (selectedTab === 'guideline' && newContextContent.length > GUIDELINE_MAX_LENGTH) {
                setError(`Content must not exceed ${GUIDELINE_MAX_LENGTH} characters`);
                setIsLoading(false);
                return;
            }

            let updatedContext;
            
            if (editingContext) {
                // Mise à jour d'un contexte existant
                const response = await api.put(`/contexts/${editingContext.id}`, {
                    title: newContextTitle,
                    content: newContextContent,
                    type: selectedTab
                });
                updatedContext = response.data;
                
                // Mettre à jour l'état local
                const updatedContexts = contexts.map(ctx => 
                    ctx.id === editingContext.id ? updatedContext : ctx
                );
                setContexts(updatedContexts);
                
                // Informer le parent de la mise à jour
                if (onContextsUpdate) {
                    onContextsUpdate(selectedTab, updatedContexts);
                }
            } else {
                // Création d'un nouveau contexte
                const response = await api.post('/contexts', {
                    project_id: projectId,
                    title: newContextTitle,
                    content: newContextContent,
                    type: selectedTab
                });
                updatedContext = response.data;
                
                // Mettre à jour l'état local
                const updatedContexts = [...contexts, updatedContext];
                setContexts(updatedContexts);
                
                // Informer le parent de la mise à jour
                if (onContextsUpdate) {
                    onContextsUpdate(selectedTab, updatedContexts);
                }
            }

            // Fermer le formulaire d'édition
            setIsEditing(false);
        } catch (err) {
            console.error('Error saving context:', err);
            setError(err.response?.data?.message || 'Error while saving');
        } finally {
            setIsLoading(false);
        }
    };

    // Supprimer un contexte
    const handleDeleteContext = async (contextId) => {
        if (!window.confirm('Do you really want to delete this context?')) {
            return;
        }

        try {
            setIsLoading(true);
            setError('');
            await api.delete(`/contexts/${contextId}`);
            
            // Mettre à jour l'état local
            const updatedContexts = contexts.filter(ctx => ctx.id !== contextId);
            setContexts(updatedContexts);
            
            // Informer le parent de la mise à jour
            if (onContextsUpdate) {
                onContextsUpdate(selectedTab, updatedContexts);
            }
        } catch (err) {
            console.error('Error deleting context:', err);
            setError('Error while deleting');
        } finally {
            setIsLoading(false);
        }
    };

    // Annuler l'édition
    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditingContext(null);
    };

    // Formatter la date de création
    const formatDate = (timestamp) => {
        return new Date(timestamp * 1000).toLocaleDateString('en-US');
    };

    return (
        <div className="context-panel">
            <div className="context-panel-header">
                <h2 className="context-panel-title">Contexts</h2>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div className="context-tabs">
                    <button 
                        className={`context-tab ${selectedTab === 'guideline' ? 'active' : ''}`}
                        onClick={() => setSelectedTab('guideline')}
                    >
                        Guidelines
                    </button>
                    <button 
                        className={`context-tab ${selectedTab === 'reference' ? 'active' : ''}`}
                        onClick={() => setSelectedTab('reference')}
                    >
                        References
                    </button>
                </div>
                
                {!isEditing && (
                    <button 
                        className="context-add-button"
                        onClick={handleAddContext}
                        disabled={isLoading}
                    >
                        <span className="material-symbols-outlined">add</span>
                    </button>
                )}
                {onClose && (
                    <button className="panel-close-button" onClick={onClose} aria-label="Close panel">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                )}
                </div>
            </div>

            {error && <div className="context-error">{error}</div>}

            {/* Info text explanation for the current tab */}
            {!isEditing && (
                <div className="info-text context-info">
                    <span className="material-symbols-outlined info-icon">info</span>
                    <span>
                        {selectedTab === 'guideline' 
                            ? "Guidelines are rules or instructions that the AI will follow when generating content. They help maintain consistency and meet specific requirements."
                            : "References are source materials that the AI can use to obtain information for your content. Add articles, research, or any content you want the AI to reference."}
                    </span>
                </div>
            )}

            {isEditing ? (
                <div className="context-form">
                    <input
                        type="text"
                        placeholder="Title"
                        value={newContextTitle}
                        onChange={(e) => setNewContextTitle(e.target.value)}
                        className="context-title-input"
                    />
                    
                    <textarea
                        placeholder={`Content ${selectedTab === 'guideline' ? `(max ${GUIDELINE_MAX_LENGTH} characters)` : ''}`}
                        value={newContextContent}
                        onChange={(e) => setNewContextContent(e.target.value)}
                        className="context-content-input"
                        maxLength={selectedTab === 'guideline' ? GUIDELINE_MAX_LENGTH : undefined}
                    />
                    
                    {selectedTab === 'guideline' && (
                        <div className="character-count">
                            {newContextContent.length}/{GUIDELINE_MAX_LENGTH}
                        </div>
                    )}
                    
                    <div className="context-form-buttons">
                        <button 
                            onClick={handleCancelEdit}
                            className="context-button cancel"
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={handleSaveContext}
                            className="context-button save"
                            disabled={isLoading || !newContextTitle.trim() || !newContextContent.trim()}
                        >
                            {isLoading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="context-list">
                    {isLoading ? (
                        <div className="context-loading">Loading...</div>
                    ) : contexts.length === 0 ? (
                        <div className="context-empty">
                            No {selectedTab === 'guideline' ? 'guideline' : 'reference'} for this project. 
                            Click + to add one.
                        </div>
                    ) : (
                        contexts.map(context => (
                            <div key={context.id} className="context-item">
                                <div className="context-item-header">
                                    <h3 className="context-item-title">{context.title}</h3>
                                    <div className="context-item-actions">
                                        <button 
                                            onClick={() => handleEditContext(context)}
                                            className="context-action-button"
                                            title="Edit"
                                        >
                                            <span className="material-symbols-outlined">edit</span>
                                        </button>
                                        <button 
                                            onClick={() => handleDeleteContext(context.id)}
                                            className="context-action-button delete"
                                            title="Delete"
                                        >
                                            <span className="material-symbols-outlined">delete</span>
                                        </button>
                                    </div>
                                </div>
                                <div className="context-item-date">
                                    Created on {formatDate(context.created_date)}
                                </div>
                                <div className="context-item-content">
                                    {context.content}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default ContextPanel; 